import { useState, useEffect, useCallback, useRef } from 'react';
import { syllabusScanService, SCAN_STATUS } from '../services/SyllabusScanService';

/**
 * React hook wrapping SyllabusScanService.
 * Provides reactive scan state for components.
 *
 * @param {string} [scanId] - Optional scanId to track a specific scan. If omitted, tracks the most recent.
 * @returns {{ status, result, error, progress, startScan, clearScan, isProcessing, isComplete, isError }}
 */
export function useSyllabusScan(scanId) {
  const [scanState, setScanState] = useState(() => {
    if (scanId) {
      const existing = syllabusScanService.getScan(scanId);
      return existing || { status: null, result: null, error: null };
    }
    return { status: null, result: null, error: null };
  });

  const trackedIdRef = useRef(scanId || null);

  // Subscribe to scan changes
  useEffect(() => {
    const handleChange = (changedScanId, scan) => {
      if (trackedIdRef.current && String(changedScanId) === String(trackedIdRef.current)) {
        setScanState({
          status: scan.status,
          result: scan.result,
          error: scan.error,
          startedAt: scan.startedAt,
          completedAt: scan.completedAt,
        });
      }
    };

    const unsub = syllabusScanService.on('change', handleChange);
    return unsub;
  }, []);

  // Update tracked ID when scanId prop changes
  useEffect(() => {
    trackedIdRef.current = scanId || null;
    if (scanId) {
      const existing = syllabusScanService.getScan(scanId);
      if (existing) {
        setScanState({
          status: existing.status,
          result: existing.result,
          error: existing.error,
          startedAt: existing.startedAt,
          completedAt: existing.completedAt,
        });
      }
    }
  }, [scanId]);

  const startScan = useCallback((documentId, options = {}) => {
    const newScanId = syllabusScanService.startScan(documentId, options);
    trackedIdRef.current = newScanId;
    setScanState({
      status: SCAN_STATUS.PENDING,
      result: null,
      error: null,
      startedAt: Date.now(),
      completedAt: null,
    });
    return newScanId;
  }, []);

  const clearScan = useCallback((id) => {
    syllabusScanService.clearScan(id || trackedIdRef.current);
    setScanState({ status: null, result: null, error: null });
  }, []);

  return {
    ...scanState,
    startScan,
    clearScan,
    isProcessing: scanState.status === SCAN_STATUS.PENDING || scanState.status === SCAN_STATUS.PROCESSING,
    isComplete: scanState.status === SCAN_STATUS.COMPLETE,
    isError: scanState.status === SCAN_STATUS.ERROR,
  };
}
